import {
  NEW_SERVICE_SQUARE_FEET_MAX,
  NEW_SERVICE_SQUARE_FEET_MIN,
  type NewServiceBuildFacts,
  type NewServicePremiseType,
  type ServiceAddress,
} from '@rheora/enrollment-contract';

import { isValidEmail } from './serviceability.service';

/**
 * E-SF20 — the new-service declaration form's logic, as data rather than JSX.
 *
 * PURE — no React, no `t()`, no API, no clock. The component renders what these
 * functions decide and resolves the returned i18n keys itself.
 *
 * ── WHY THE BOUNDS ARE IMPORTED, NOT RESTATED ──────────────────────────────
 * `NEW_SERVICE_SQUARE_FEET_MIN/MAX` come from the contract. A form that hard-coded
 * "200" and "25000" would drift from the server the first time the band moved, and
 * the customer would meet the difference as an unexplained 400 after filling the
 * whole form. The client's job here is a better message, never a second opinion.
 */

/** The premise types, in the order the form offers them (most common first). */
export const NEW_SERVICE_PREMISE_TYPES: readonly NewServicePremiseType[] = [
  'SINGLE_FAMILY',
  'TOWNHOME',
  'MULTI_FAMILY',
  'MANUFACTURED',
];

/** i18n key for a premise type's label. */
export function premiseTypeLabelKey(type: NewServicePremiseType): string {
  return `shop.newService.premiseType.${type}`;
}

/**
 * Whether this premise type is scaled by UNIT COUNT rather than floor area.
 *
 * Multi-family is the odd one out, and it is not merely a different input: a
 * declaration with more than one unit can never resolve to a single premise,
 * because the TDU publishes one ESI ID per unit on the same street address. The
 * form asks the question that makes that visible as early as possible.
 */
export function scalesByUnitCount(type: NewServicePremiseType): boolean {
  return type === 'MULTI_FAMILY';
}

/**
 * Parse an optional numeric form field.
 *
 * @returns the integer, `null` for "the customer left it blank" (which is
 *   legitimate on brand-new construction), or `'invalid'` for text that is not a
 *   number at all. The three cases are distinct because blank and wrong must
 *   produce different outcomes: blank proceeds on a documented default, wrong
 *   stops and asks.
 */
export function parseOptionalCount(raw: string): number | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (!/^\d{1,7}$/u.test(trimmed)) return 'invalid';
  return Number.parseInt(trimmed, 10);
}

export interface NewServiceFormInput {
  /**
   * Street address. Collected ONLY on the ZIP on-ramp, where the whole reason the
   * customer is here is that their address is not in the suggestion list — a
   * brand-new premise has no ESI ID, so it can never appear there. Ignored (and
   * unvalidated) when the caller already holds a resolved address.
   */
  readonly line1: string;
  readonly city: string;
  readonly premiseType: NewServicePremiseType | '';
  readonly squareFeet: string;
  readonly unitCount: string;
  readonly expectedOccupancyDate: string;
  /**
   * US-14 — the municipal electrical permit, if the customer has it.
   *
   * Not decoration and not analytics: the inspection green tag is the most common
   * cause of new-service delay and the one thing the customer can act on. It is
   * what lets a delay notice say something useful instead of "still waiting".
   */
  readonly permitNumber: string;
  readonly builderName: string;
  readonly email: string;
}

export interface NewServiceFormError {
  /** Which control to focus / mark. */
  readonly field: 'line1' | 'city' | 'premiseType' | 'squareFeet' | 'unitCount' | 'email';
  /** i18n key — the component resolves it; this module never calls `t()`. */
  readonly messageKey: string;
}

export interface NewServiceValidateOptions {
  /**
   * Whether the form is collecting the street address itself. False when the
   * caller already resolved one (the address on-ramp), true on the ZIP on-ramp.
   */
  readonly collectsAddress: boolean;
}

/**
 * Validate the form. @returns the FIRST error, or `null` when it is submittable.
 *
 * First-error-only rather than a list: the form is short, the fields are ordered,
 * and showing four messages at once on a five-field form reads as a rejection
 * rather than a correction.
 */
export function validateNewServiceForm(
  input: NewServiceFormInput,
  options: NewServiceValidateOptions = { collectsAddress: false },
): NewServiceFormError | null {
  if (options.collectsAddress) {
    if (input.line1.trim().length === 0) {
      return { field: 'line1', messageKey: 'shop.newService.line1.required' };
    }
    if (input.city.trim().length === 0) {
      return { field: 'city', messageKey: 'shop.newService.city.required' };
    }
  }

  if (input.premiseType === '') {
    return { field: 'premiseType', messageKey: 'shop.newService.premiseType.required' };
  }

  if (scalesByUnitCount(input.premiseType)) {
    const units = parseOptionalCount(input.unitCount);
    if (units === 'invalid' || (units !== null && (units < 1 || units > 500))) {
      return { field: 'unitCount', messageKey: 'shop.newService.unitCount.invalid' };
    }
  } else {
    const sqft = parseOptionalCount(input.squareFeet);
    if (
      sqft === 'invalid' ||
      (sqft !== null && (sqft < NEW_SERVICE_SQUARE_FEET_MIN || sqft > NEW_SERVICE_SQUARE_FEET_MAX))
    ) {
      return { field: 'squareFeet', messageKey: 'shop.newService.squareFeet.invalid' };
    }
  }

  if (!isValidEmail(input.email)) {
    return { field: 'email', messageKey: 'shop.newService.email.invalid' };
  }

  return null;
}

/**
 * Build the wire `ServiceAddress` from a validated form plus the known ZIP.
 *
 * The ZIP comes from the on-ramp (the customer already chose it) rather than from
 * the form, so it cannot disagree with the ZIP whose suggestions just failed to
 * contain their address — which is the entire premise of being on this path.
 *
 * ⚠️ CALL ONLY AFTER `validateNewServiceForm({ collectsAddress: true })` PASSES.
 */
export function toServiceAddress(input: NewServiceFormInput, zip: string): ServiceAddress {
  return { line1: input.line1.trim(), city: input.city.trim(), zip };
}

/**
 * Build the wire `buildFacts` from a validated form.
 *
 * Absent optionals are OMITTED rather than sent as null/empty — the contract's
 * fields are `.optional()`, and an explicit empty string would fail the min-length
 * bound on `permitNumber`/`builderName` while an explicit `null` is simply not the
 * declared shape. Only the fields relevant to the chosen premise type are sent, so
 * a square footage typed before switching to multi-family does not travel.
 *
 * ⚠️ CALL ONLY AFTER `validateNewServiceForm` RETURNS NULL. It coerces rather than
 * re-checks; the validation above is the gate.
 */
export function toBuildFacts(input: NewServiceFormInput): NewServiceBuildFacts {
  if (input.premiseType === '') {
    throw new Error('toBuildFacts called on an unvalidated form (no premise type)');
  }

  const byUnits = scalesByUnitCount(input.premiseType);
  const sqft = byUnits ? null : parseOptionalCount(input.squareFeet);
  const units = byUnits ? parseOptionalCount(input.unitCount) : null;
  const occupancy = input.expectedOccupancyDate.trim();
  const permit = input.permitNumber.trim();
  const builder = input.builderName.trim();

  return {
    premiseType: input.premiseType,
    ...(typeof sqft === 'number' ? { squareFeet: sqft } : {}),
    ...(typeof units === 'number' ? { unitCount: units } : {}),
    ...(occupancy.length === 0 ? {} : { expectedOccupancyDate: occupancy }),
    ...(permit.length === 0 ? {} : { permitNumber: permit }),
    ...(builder.length === 0 ? {} : { builderName: builder }),
  };
}
